import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
import _get from 'lodash.get';
import { useCallback } from 'react';

import AdyenApplePayButton from '../AdyenApplePayButton/AdyenApplePayButton';
import { ApplePayErrorType } from '../AdyenApplePayButton/AdyenApplePayButton.static';

import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { Nullable } from '@types-internal';
import { AdyenConfig, AdyenEnvironment } from '@types-internal/AdyenTypes';

interface InnerAdyenApplePayButtonProps extends Omit<AdyenConfig, 'clientKey'> {
  countryCode: string;
  currency: string;
  amount: number;
  paymentMethodsResponse: PaymentMethodsResponse;
  onSubmit: (applePayToken: string) => void;
  onError: (errorType: Nullable<string>) => void;
}

function getApplePayErrorType(errorType: Nullable<string>): ApplePayErrorType {
  return errorType === 'CANCEL' ? 'payment' : 'initialization';
}

const InnerAdyenApplePayButton: React.FC<InnerAdyenApplePayButtonProps> = (props) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  const {
    config: {
      adyen: { clientKey },
    },
  } = useConfig();

  const onFatalError = useCallback(
    (error: unknown) => {
      const errorType: Nullable<string> = _get(error, 'name', null);
      setError({
        message: t(`checkout.payment.applepay.error.${getApplePayErrorType(errorType)}`),
      });

      if (errorType !== 'CANCEL') {
        captureException({ error });
      }

      props.onError(errorType);
    },
    [props.onError],
  );

  return <AdyenApplePayButton {...props} clientKey={clientKey} onFatalError={onFatalError} />;
};

export interface AdyenApplePayButtonWrapperProps extends CoreProvidersProps {
  adyenEnvironment: AdyenEnvironment;
  countryCode: string;
  currency: string;
  amount: number;
  paymentMethodsResponse: PaymentMethodsResponse;
  onSubmit: (applePayToken: string) => void;
  onError: (errorType: Nullable<string>) => void;
}

/*
 * Wrapper over the true component, because we first need to call the CoreProviders.
 */
const AdyenApplePayButtonWrapper: React.FC<AdyenApplePayButtonWrapperProps> = ({
  env,
  shopBundle,
  locale,
  domain,
  errorContainerId,
  adyenEnvironment,
  countryCode,
  currency,
  amount,
  paymentMethodsResponse,
  onSubmit,
  onError,
}) => {
  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      featureName="applepay">
      <InnerAdyenApplePayButton
        adyenEnvironment={adyenEnvironment}
        countryCode={countryCode}
        currency={currency}
        amount={amount}
        paymentMethodsResponse={paymentMethodsResponse}
        onSubmit={onSubmit}
        onError={onError}
      />
    </CoreProviders>
  );
};

export default AdyenApplePayButtonWrapper;
